import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSubscription } from '../lib/featureFlags';
import { getBranding, logoSrcOnDark } from '../utils/branding';

const Sidebar = ({ 
  userProfile,
  agencySettings,
  showPropertiesView,
  showContactsView,
  showDemandesContactView,
  showFavoritesView,
  showMarketingView,
  showOutilsView,
  setActiveMarketingTab,
  setActiveOutilsTab,
  startNewProperty,
  startNewContact,
  showDashboardView,
  showAvisDeValeurView,
  demandesContactCount = 0,
  onClose, // Nouvelle prop pour fermer le menu mobile
  sousMenuOuvert = null, // lot DK2 — sous-menu à ouvrir quand le menu est déplié depuis la colonne d'icônes
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: subscriptionData } = useSubscription();
  // Branding agence (niveau ≥ 2 → logo agence dans l'en-tête sidebar)
  const [branding, setBranding] = useState(() => getBranding());
  useEffect(() => {
    const sync = () => setBranding(getBranding());
    window.addEventListener('branding-applied', sync);
    return () => window.removeEventListener('branding-applied', sync);
  }, []);
  const useAgenceLogo = branding && branding.branding_level >= 2 && (branding.logo_url_dark || branding.logo_url);
  const activeModules = subscriptionData?.subscription?.active_modules || null; // null = loading, ne grise rien
  const isModuleActive = (slug) => {
    if (activeModules === null) return true; // fail-open pendant loading
    return activeModules.includes(slug);
  };
  const [openMenus, setOpenMenus] = useState(() => {
    // Auto-ouvrir le sous-menu correspondant à la route active
    const path = window.location.pathname;
    const initial = {};
    if (path.startsWith('/contacts') || path.startsWith('/demandes-contact')) {
      initial['contacts-crm'] = true;
    }
    if (path.startsWith('/properties') || path.startsWith('/biens')) {
      initial['mon-stock'] = true;
    }
    if (path.startsWith('/documents')) {
      initial['documents'] = true;
    }
    if (path.startsWith('/marketing')) {
      initial['marketing'] = true;
    }
    // lot DI — section Statistiques (Observatoire + Simulateurs)
    if (path.startsWith('/observatoire') || path.startsWith('/outils/simulateurs')) {
      initial['statistiques'] = true;
    }
    return initial;
  });
  const [showUserMenu, setShowUserMenu] = useState(false);
  const userMenuRef = useRef(null);
  // Réponses du support non lues → pastille sur le menu utilisateur.
  const [supportUnread, setSupportUnread] = useState(0);

  useEffect(() => {
    let vivant = true;
    const lire = async () => {
      try {
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/support/unread-count`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (!r.ok) return;
        const d = await r.json();
        if (vivant) setSupportUnread(d.unread || 0);
      } catch (_) { /* silencieux : une pastille absente ne doit rien casser */ }
    };
    lire();
    const t = setInterval(lire, 45000);
    // Relevé aussi au retour sur l'onglet : la réponse du support arrive souvent pendant
    // que l'utilisateur est ailleurs.
    window.addEventListener('focus', lire);
    return () => { vivant = false; clearInterval(t); window.removeEventListener('focus', lire); };
  }, [location.pathname]);


  
  // Helper pour naviguer et fermer le menu sur mobile/tablette
  const handleNavigate = (path) => {
    navigate(path);
    if (onClose) onClose();
  };

  // lot DK2 — ouverture du sous-menu demandé par la colonne d'icônes
  useEffect(() => {
    if (sousMenuOuvert) setOpenMenus((p) => ({ ...p, [sousMenuOuvert]: true }));
  }, [sousMenuOuvert]);

  const toggleMenu = (menuName) => {    setOpenMenus(prev => ({
      ...prev,
      [menuName]: !prev[menuName]
    }));
  };

  const toggleUserMenu = () => {
    setShowUserMenu(!showUserMenu);
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <aside className="flex flex-col w-full md:w-72 bg-[#0F172A] text-gray-400 border-r border-slate-800 shadow-2xl h-screen shrink-0" style={{fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'}}>
      {/* Logo / Header */}
      <div className="flex items-center justify-center px-6 py-3 border-b border-slate-800 shrink-0">
        <img
          src={useAgenceLogo ? logoSrcOnDark(branding) : '/logo_komerces_fonce.svg'}
          alt={useAgenceLogo ? (branding.nom_affiche || branding.name || 'Agence') : 'Komerces'}
          className="w-4/5 h-auto max-h-14 object-contain"
          onError={(e) => { e.currentTarget.src = '/logo_komerces_fonce.svg'; }}
        />
      </div>

      {/* Navigation Menu - Scrollable */}
      <nav className="flex-1 overflow-y-auto py-4 px-3">
        
        {/* Avis de Valeur - Featured (masqué si non actif) */}
        {isModuleActive('avis-de-valeur') && (
          <a 
            href="#" 
            onClick={(e) => { 
              e.preventDefault(); 
              handleNavigate('/outils/avis-de-valeur');
            }}
            data-testid="sidebar-avis-valeur"
            className="sidebar-item flex items-center gap-3 px-4 py-3 mb-2 rounded-lg bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 shadow-lg shadow-violet-900/40 ring-1 ring-violet-400/30 transition group"
          >
            <i className="bi bi-calculator text-white text-xl"></i>
            <span className="text-white font-semibold text-base">Avis de Valeur</span>
            <span className="ml-auto text-[10px] font-bold px-1.5 py-0.5 rounded bg-white/20 text-white border border-white/30">IA</span>
          </a>
        )}

        {/* Tableau de bord - Active */}
        <a 
          href="#" 
          onClick={(e) => { 
            e.preventDefault(); 
            showDashboardView?.();
          }}
          className="sidebar-item active flex items-center gap-3 px-4 py-2.5 mb-1 rounded-lg"
        >
          <i className="bi bi-speedometer2 text-white text-xl"></i>
          <span className="text-white font-normal text-base">Tableau de bord</span>
        </a>

        {/* Section MÉTIER */}
        <div className="my-4">
          <p className="text-[#94A3B8] text-[12px] font-semibold uppercase tracking-wider px-4 mb-2 text-left">Métier</p>
        </div>

        {/* Mon Stock (Biens & Mandats) */}
        <div>
          <button 
            onClick={() => toggleMenu('mon-stock')} 
            className="sidebar-item w-full flex items-center justify-between px-4 py-2.5 mb-1 rounded-lg transition"
          >
            <div className="flex items-center gap-3">
              <i className="bi bi-shop text-white text-xl"></i>
              <span className="text-white font-normal text-base">Mon Stock</span>
            </div>
            <i className={`bi bi-chevron-down text-[#94A3B8] text-xs transition-transform ${openMenus['mon-stock'] ? 'rotate-180' : ''}`}></i>
          </button>
          {openMenus['mon-stock'] && (
            <ul className="submenu mb-1">
              <li>
                <a 
                  href="#" 
                  onClick={(e) => { 
                    e.preventDefault(); 
                    showPropertiesView?.();
                  }} 
                  className="submenu-item flex items-center gap-3 px-4 py-1 pl-12 text-[#CED4DC] hover:text-white rounded-lg transition"
                >
                  <i className="bi bi-list-ul text-xl"></i> <span className="text-sm">Liste des biens</span>
                </a>
              </li>
              <li>
                <a 
                  href="#" 
                  data-testid="sidebar-mon-stock-diffusion"
                  onClick={(e) => { 
                    e.preventDefault();
                    navigate('/diffusion');
                    onClose?.();
                  }} 
                  className="submenu-item flex items-center gap-3 px-4 py-1 pl-12 text-[#CED4DC] hover:text-white rounded-lg transition"
                  title="Vue globale de la diffusion sur les supports externes"
                >
                  <i className="bi bi-broadcast text-xl"></i> <span className="text-sm">Diffusion</span>
                </a>
              </li>
              <li>
                <a 
                  href="#" 
                  onClick={(e) => { 
                    e.preventDefault();
                    // Navigation vers Wizard (flux MVP)
                    navigate('/properties/wizard/new');
                    onClose?.(); // Fermer la sidebar mobile si ouverte
                  }} 
                  className="submenu-item flex items-center gap-3 px-4 py-1 pl-12 text-[#CED4DC] hover:text-white rounded-lg transition"
                  title="Création rapide via assistant guidé"
                >
                  <i className="bi bi-plus-circle text-xl"></i> <span className="text-sm">Ajouter un bien</span>
                </a>
              </li>
              <li>
                <a 
                  href="#" 
                  onClick={(e) => { e.preventDefault(); showPropertiesView?.(); }} 
                  className="submenu-item flex items-center gap-3 px-4 py-1 pl-12 text-[#CED4DC] hover:text-white rounded-lg transition"
                >
                  <i className="bi bi-grid text-xl"></i> <span className="text-sm">Catégories</span>
                </a>
              </li>
              <li>
                <a 
                  href="#" 
                  onClick={(e) => { e.preventDefault(); showPropertiesView?.(); }} 
                  className="submenu-item flex items-center gap-3 px-4 py-1 pl-12 text-[#CED4DC] hover:text-white rounded-lg transition"
                >
                  <i className="bi bi-search text-xl"></i> <span className="text-sm">Avancée</span>
                </a>
              </li>
            </ul>
          )}
        </div>

        {/* Contacts & CRM */}
        <div>
          <button 
            onClick={() => toggleMenu('contacts-crm')} 
            className="sidebar-item w-full flex items-center justify-between px-4 py-2.5 mb-1 rounded-lg transition"
          >
            <div className="flex items-center gap-3">
              <i className="bi bi-people text-white text-xl"></i>
              <span className="text-white font-normal text-base">Contacts & CRM</span>
            </div>
            <i className={`bi bi-chevron-down text-[#94A3B8] text-xs transition-transform ${openMenus['contacts-crm'] ? 'rotate-180' : ''}`}></i>
          </button>
          {openMenus['contacts-crm'] && (
            <ul className="submenu mb-1">
              <li>
                <a 
                  href="#" 
                  onClick={(e) => { 
                    e.preventDefault(); 
                    showContactsView?.();
                  }} 
                  className="submenu-item flex items-center gap-3 px-4 py-1 pl-12 text-[#CED4DC] hover:text-white rounded-lg transition"
                >
                  <i className="bi bi-person-lines-fill text-xl"></i> <span className="text-sm">Tous les contacts</span>
                </a>
              </li>
              <li>
                <a 
                  href="#" 
                  onClick={(e) => { 
                    e.preventDefault(); 
                    startNewContact?.(); 
                  }} 
                  className="submenu-item flex items-center gap-3 px-4 py-1 pl-12 text-[#CED4DC] hover:text-white rounded-lg transition"
                >
                  <i className="bi bi-person-plus text-xl"></i> <span className="text-sm">Ajouter</span>
                </a>
              </li>
              <li>
                <a 
                  href="#" 
                  onClick={(e) => { 
                    e.preventDefault(); 
                    showDemandesContactView?.();
                  }} 
                  className="submenu-item flex items-center justify-between px-4 py-1 pl-12 text-[#CED4DC] hover:text-white rounded-lg transition"
                >
                  <div className="flex items-center gap-3">
                    <i className="bi bi-envelope-fill text-xl"></i> <span className="text-sm">Demandes</span>
                  </div>
                  {demandesContactCount > 0 && (
                    <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-bold">{demandesContactCount}</span>
                  )}
                </a>
              </li>
              <li>
                <a 
                  href="#" 
                  onClick={(e) => { 
                    e.preventDefault(); 
                    handleNavigate('/groupes');
                  }} 
                  data-testid="sidebar-groupes"
                  className="submenu-item flex items-center gap-3 px-4 py-1 pl-12 text-[#CED4DC] hover:text-white rounded-lg transition"
                >
                  <i className="bi bi-collection text-xl"></i> <span className="text-sm">Groupes</span>
                </a>
              </li>
              {/* lot DK2 — « Tâches » est revenu dans la barre du haut, visible aussi sur téléphone */}
            </ul>
          )}
        </div>

        {/* Documents internes (socle — toujours actif) */}
        <div>
          <button 
            onClick={() => toggleMenu('documents')} 
            className="sidebar-item w-full flex items-center justify-between px-4 py-2.5 mb-1 rounded-lg transition"
            data-testid="sidebar-documents"
          >
            <div className="flex items-center gap-3">
              <i className="bi bi-folder-fill text-white text-xl"></i>
              <span className="text-white font-normal text-base">Documents</span>
            </div>
            <i className={`bi bi-chevron-down text-[#94A3B8] text-xs transition-transform ${openMenus['documents'] ? 'rotate-180' : ''}`}></i>
          </button>
          {openMenus['documents'] && (
            <ul className="submenu mb-1">
              <li>
                <a 
                  href="#" 
                  onClick={(e) => { e.preventDefault(); handleNavigate('/documents'); }} 
                  data-testid="sidebar-documents-bibliotheque"
                  className="submenu-item flex items-center gap-3 px-4 py-1 pl-12 text-[#CED4DC] hover:text-white rounded-lg transition"
                >
                  <i className="bi bi-folder-fill text-xl"></i> <span className="text-sm">Tous les documents</span>
                </a>
              </li>
              <li>
                <a 
                  href="#" 
                  onClick={(e) => { e.preventDefault(); handleNavigate('/documents/types'); }} 
                  data-testid="sidebar-documents-types"
                  className="submenu-item flex items-center gap-3 px-4 py-1 pl-12 text-[#CED4DC] hover:text-white rounded-lg transition"
                >
                  <i className="bi bi-collection text-xl"></i> <span className="text-sm">Documents types</span>
                </a>
              </li>
              {isModuleActive('documents-ia') && (
                <li>
                  <a 
                    href="#" 
                    onClick={(e) => { e.preventDefault(); handleNavigate('/documents/generer'); }} 
                    data-testid="sidebar-documents-generer"
                    className="submenu-item flex items-center gap-3 px-4 py-1 pl-12 text-[#CED4DC] hover:text-white rounded-lg transition"
                  >
                    <i className="bi bi-magic text-xl"></i> <span className="text-sm">Générer (IA)</span>
                  </a>
                </li>
              )}
            </ul>
          )}
        </div>

        {/* Section MARKETING */}
        {isModuleActive('marketing') ? (
          <>
            <div className="my-4">
              <p className="text-[#94A3B8] text-[12px] font-semibold uppercase tracking-wider px-4 mb-2 text-left">Marketing</p>
            </div>

            {/* Marketing */}
            <div>
              <button 
                onClick={() => toggleMenu('marketing')} 
                className="sidebar-item w-full flex items-center justify-between px-4 py-2.5 mb-1 rounded-lg transition"
                data-testid="sidebar-marketing"
              >
                <div className="flex items-center gap-3">
                  <i className="bi bi-megaphone text-white text-xl"></i>
                  <span className="text-white font-normal text-base">Marketing</span>
                </div>
                <i className={`bi bi-chevron-down text-[#94A3B8] text-xs transition-transform ${openMenus['marketing'] ? 'rotate-180' : ''}`}></i>
              </button>
          {openMenus['marketing'] && (
            <ul className="submenu mb-1">
              <li>
                <a 
                  href="#" 
                  onClick={(e) => { 
                    e.preventDefault(); 
                    handleNavigate('/marketing?tab=dashboard');
                  }} 
                  data-testid="sidebar-marketing-dashboard"
                  className="submenu-item flex items-center gap-3 px-4 py-1 pl-12 text-[#CED4DC] hover:text-white rounded-lg transition"
                >
                  <i className="bi bi-grid-fill text-xl"></i> <span className="text-sm">Tableau de bord</span>
                </a>
              </li>
              <li>
                <a 
                  href="#" 
                  onClick={(e) => { 
                    e.preventDefault(); 
                    handleNavigate('/marketing?tab=campaigns');
                  }} 
                  data-testid="sidebar-marketing-campaigns"
                  className="submenu-item flex items-center gap-3 px-4 py-1 pl-12 text-[#CED4DC] hover:text-white rounded-lg transition"
                >
                  <i className="bi bi-megaphone-fill text-xl"></i> <span className="text-sm">Campagnes</span>
                </a>
              </li>
              <li>
                <a 
                  href="#" 
                  onClick={(e) => { 
                    e.preventDefault(); 
                    handleNavigate('/marketing?tab=social');
                  }} 
                  data-testid="sidebar-marketing-social"
                  className="submenu-item flex items-center gap-3 px-4 py-1 pl-12 text-[#CED4DC] hover:text-white rounded-lg transition"
                >
                  <i className="bi bi-share-fill text-xl"></i> <span className="text-sm">Réseaux sociaux</span>
                </a>
              </li>
              <li>
                <a 
                  href="#" 
                  onClick={(e) => { 
                    e.preventDefault(); 
                    handleNavigate('/marketing?tab=analytics');
                  }} 
                  data-testid="sidebar-marketing-analytics"
                  className="submenu-item flex items-center gap-3 px-4 py-1 pl-12 text-[#CED4DC] hover:text-white rounded-lg transition"
                >
                  <i className="bi bi-graph-up text-xl"></i> <span className="text-sm">Analytics</span>
                </a>
              </li>
              <li>
                <a 
                  href="#" 
                  onClick={(e) => { 
                    e.preventDefault(); 
                    handleNavigate('/marketing?tab=chatbot');
                  }} 
                  data-testid="sidebar-marketing-chatbot"
                  className="submenu-item flex items-center gap-3 px-4 py-1 pl-12 text-[#CED4DC] hover:text-white rounded-lg transition"
                >
                  <i className="bi bi-robot text-xl"></i> <span className="text-sm">Chatbot</span>
                </a>
              </li>
            </ul>
          )}
        </div>
          </>
        ) : (
          /* Marketing désactivé : section masquée (n'apparaît pas en haut de la nav) */
          null
        )}

        {/* Section OUTILS */}
        <div className="my-4">
          <p className="text-[#94A3B8] text-[12px] font-semibold uppercase tracking-wider px-4 mb-2 text-left">Outils</p>
        </div>

        {/* Statistiques (gated stats-simulateurs) — lot DI : entrée dépliable
            avec Observatoire (BODACC) et Simulateurs (page inchangée). */}
        {isModuleActive('stats-simulateurs') ? (
          <div>
            <button
              onClick={() => toggleMenu('statistiques')}
              className="sidebar-item w-full flex items-center justify-between px-4 py-2.5 mb-1 rounded-lg transition"
              data-testid="sidebar-statistiques"
            >
              <div className="flex items-center gap-3">
                <i className="bi bi-graph-up text-white text-xl"></i>
                <span className="text-white font-normal text-base">Statistiques</span>
              </div>
              <i className={`bi bi-chevron-down text-[#94A3B8] text-xs transition-transform ${openMenus['statistiques'] ? 'rotate-180' : ''}`}></i>
            </button>
            {openMenus['statistiques'] && (
              <ul className="submenu mb-1">
                <li>
                  <a
                    href="/observatoire"
                    onClick={(e) => { e.preventDefault(); handleNavigate('/observatoire'); }}
                    data-testid="sidebar-observatoire"
                    className="submenu-item flex items-center gap-3 px-4 py-1 pl-12 text-[#CED4DC] hover:text-white rounded-lg transition"
                  >
                    <i className="bi bi-bar-chart-line text-xl"></i> <span className="text-sm">Observatoire</span>
                  </a>
                </li>
                <li>
                  <a
                    href="/outils/simulateurs"
                    onClick={(e) => { e.preventDefault(); handleNavigate('/outils/simulateurs'); }}
                    data-testid="sidebar-simulateurs"
                    className="submenu-item flex items-center gap-3 px-4 py-1 pl-12 text-[#CED4DC] hover:text-white rounded-lg transition"
                  >
                    <i className="bi bi-calculator text-xl"></i> <span className="text-sm">Simulateurs</span>
                  </a>
                </li>
              </ul>
            )}
          </div>
        ) : (
          /* Module stats-simulateurs OFF → section masquée */
          null
        )}

        {/* Statistiques : retiré du menu (page non prête).
            Le dashboard d'accueil (route `/`) couvre les KPI essentiels. */}

      </nav>

      {/* Footer / User Profile */}
      <div className="border-t border-slate-800 p-4 relative" ref={userMenuRef}>
        <button 
          onClick={toggleUserMenu}
          className="w-full flex items-center justify-between hover:bg-slate-800/50 p-2 rounded-lg transition relative"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
              {userProfile?.prenom?.[0] || 'U'}{userProfile?.nom?.[0] || ''}
            </div>
            <div className="text-left min-w-0">
              <p className="text-white text-sm font-semibold truncate">
                {userProfile?.prenom || 'Utilisateur'} {userProfile?.nom || ''}
              </p>
              {agencySettings?.nom_agence && (
                <p className="text-[#94A3B8] text-xs truncate">{agencySettings.nom_agence}</p>
              )}
            </div>
          </div>
          <i className={`bi bi-chevron-up text-[#94A3B8] text-sm transition-transform ${showUserMenu ? 'rotate-180' : ''}`}></i>
          {/* Pastille : réponses du support non lues. Elle se vide à l'ouverture de
              « Mes signalements » (POST /support/mark-read). */}
          {supportUnread > 0 && (
            <span
              title={`${supportUnread} réponse(s) du support non lue(s)`}
              data-testid="usermenu-support-badge"
              className="absolute top-0 right-1 min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center shadow ring-2 ring-slate-900"
            >
              {supportUnread > 9 ? '9+' : supportUnread}
            </span>
          )}
        </button>
        
        {/* User Menu Dropdown */}
        {showUserMenu && (
          <div className="absolute bottom-full left-0 right-0 mb-2 mx-4 bg-[#1E293B] border border-slate-700 rounded-lg shadow-2xl overflow-hidden">
            <a 
              href="#" 
              onClick={(e) => { e.preventDefault(); handleNavigate('/profil'); setShowUserMenu(false); }}
              className="flex items-center gap-3 px-4 py-3 text-[#CED4DC] hover:bg-slate-700 hover:text-white transition"
              data-testid="usermenu-mon-profil"
            >
              <i className="bi bi-person text-xl"></i>
              <span className="text-sm">Mon Profil</span>
            </a>
            <a 
              href="#" 
              onClick={(e) => { e.preventDefault(); handleNavigate('/mon-abonnement'); setShowUserMenu(false); }}
              className="flex items-center gap-3 px-4 py-3 text-[#CED4DC] hover:bg-slate-700 hover:text-white transition border-t border-slate-700/50"
              data-testid="usermenu-mon-abonnement"
            >
              <i className="bi bi-credit-card-2-front text-xl"></i>
              <span className="text-sm">Mon abonnement</span>
            </a>
            <a 
              href="#" 
              onClick={(e) => { e.preventDefault(); handleNavigate('/settings'); setShowUserMenu(false); }}
              className="flex items-center gap-3 px-4 py-3 text-[#CED4DC] hover:bg-slate-700 hover:text-white transition border-t border-slate-700/50"
              data-testid="usermenu-parametres"
            >
              <i className="bi bi-gear text-xl"></i>
              <span className="text-sm">Paramètres</span>
            </a>
            <a 
              href="#" 
              onClick={(e) => { e.preventDefault(); handleNavigate('/support'); setShowUserMenu(false); }}
              className="flex items-center gap-3 px-4 py-3 text-[#CED4DC] hover:bg-slate-700 hover:text-white transition border-t border-slate-700/50"
              data-testid="usermenu-mes-signalements"
            >
              <i className="bi bi-life-preserver text-xl"></i>
              <span className="text-sm">Mes signalements</span>
              {supportUnread > 0 && (
                <span
                  data-testid="usermenu-support-badge-item"
                  className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[11px] font-bold flex items-center justify-center"
                >
                  {supportUnread > 9 ? '9+' : supportUnread}
                </span>
              )}
            </a>
            {/* lot DK — téléphone : la bulle flottante est masquée, l'entrée la remplace */}
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                setShowUserMenu(false);
                onClose?.();
                window.dispatchEvent(new Event('ouvrir-signalement'));
              }}
              className="md:hidden flex items-center gap-3 px-4 py-3 text-[#CED4DC] hover:bg-slate-700 hover:text-white transition border-t border-slate-700/50"
              data-testid="usermenu-signaler-probleme"
            >
              <i className="bi bi-exclamation-circle text-xl"></i>
              <span className="text-sm">Signaler un problème</span>
            </a>
            <a 
              href="#" 
              onClick={(e) => { e.preventDefault(); handleNavigate('/logout'); setShowUserMenu(false); }}
              className="flex items-center gap-3 px-4 py-3 text-red-400 hover:bg-red-500/10 hover:text-red-300 transition border-t border-slate-700/50"
              data-testid="usermenu-deconnexion"
            >
              <i className="bi bi-box-arrow-right text-xl"></i>
              <span className="text-sm">Déconnexion</span>
            </a>
          </div>
        )}
      </div>

    </aside>
  );
};

export default Sidebar;
